import { useEffect, useRef, useState } from 'react';

import { useAuth } from '../context/AuthContext';
import { inboundRentalsAPI } from '../lib/inboundRentalsApi';

/**
 * Les camions « rendus à leur propriétaire » à une date donnée.
 *
 * Quand la société loue des camions chez un transporteur, un camion dont le
 * contrat est terminé ne peut plus partir: le serveur refuse le trajet. Ici on
 * prévient l'exploitant AVANT, au moment où il choisit le camion, au lieu de lui
 * laisser découvrir le refus à l'enregistrement.
 *
 * Le contrôle ne s'exécute que si la licence comprend la location de camions.
 * Si le serveur ne répond pas, on n'invente rien: le camion reste choisissable
 * et le serveur garde le dernier mot.
 */

/** Texte affiché quand le serveur ne donne pas de raison. */
export const MESSAGE_CAMION_RENDU = 'Rendu au propriétaire — ne peut plus partir';

/** Une date `AAAA-MM-JJ` (ou un horodatage ISO) ramenée à sa partie date; vide si absente. */
export const dateDuJour = (valeur) => {
  const texte = String(valeur || '').trim();
  return /^\d{4}-\d{2}-\d{2}/.test(texte) ? texte.slice(0, 10) : '';
};

const cleDe = (date, truckId) => `${date}|${truckId}`;

/** Les réponses du serveur rangées par camion: `{ [truck_id]: reponse }`. */
export const indexerVerification = (reponses) => {
  const resultat = {};
  (Array.isArray(reponses) ? reponses : []).forEach((ligne) => {
    if (ligne && ligne.truck_id) resultat[ligne.truck_id] = ligne;
  });
  return resultat;
};

/** Vrai si la réponse dit que le camion n'est plus dans la flotte. */
export const estRendu = (verification) => Boolean(verification) && verification.in_fleet === false;

/** La raison à montrer à l'exploitant pour un camion rendu. */
export const raisonRendu = (verification) =>
  (verification && verification.message) || MESSAGE_CAMION_RENDU;

const moduleLocationActif = () => {
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return Boolean(useAuth().hasModule('truck_rentals'));
  } catch (erreur) {
    // Hors fournisseur d'authentification (écran isolé): pas de contrôle.
    return false;
  }
};

/**
 * `camionsIds`: les camions proposés ou choisis. `date`: la date de départ
 * (`AAAA-MM-JJ`); vide = aujourd'hui côté serveur.
 *
 * Renvoie `{ verification(truckId), estRendu(truckId), raison(truckId), actif }`.
 * Un camion pas encore contrôlé est traité comme disponible.
 */
export function useVerificationFlotte({ camionsIds = [], date = '', actif = true } = {}) {
  const moduleActif = moduleLocationActif();
  const controle = Boolean(actif && moduleActif);
  const jour = dateDuJour(date);
  const [reponses, setReponses] = useState({});
  const dejaDemandes = useRef(new Set());
  const monte = useRef(true);

  useEffect(() => {
    monte.current = true;
    return () => {
      monte.current = false;
    };
  }, []);

  // Une clé stable: la liste change de forme à chaque rendu, pas de contenu.
  const ids = Array.from(new Set((camionsIds || []).filter(Boolean)));
  const clefIds = ids.join(',');

  useEffect(() => {
    if (!controle || ids.length === 0) return undefined;
    const aDemander = ids.filter((id) => !dejaDemandes.current.has(cleDe(jour, id)));
    if (aDemander.length === 0) return undefined;
    aDemander.forEach((id) => dejaDemandes.current.add(cleDe(jour, id)));

    // Les réponses sont rangées par date et par camion: une réponse tardive ne
    // peut pas fausser un autre jour, on ne l'annule donc pas.
    inboundRentalsAPI
      .fleetCheck(aDemander, jour || undefined)
      .then((donnees) => {
        if (!monte.current) return;
        const parCamion = indexerVerification(donnees);
        setReponses((precedent) => {
          const suite = { ...precedent };
          Object.entries(parCamion).forEach(([id, ligne]) => {
            suite[cleDe(jour, id)] = ligne;
          });
          return suite;
        });
      })
      .catch(() => {
        // Le serveur ne répond pas: on laisse redemander au prochain changement.
        aDemander.forEach((id) => dejaDemandes.current.delete(cleDe(jour, id)));
      });
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controle, jour, clefIds]);

  const verification = (truckId) => (controle ? reponses[cleDe(jour, truckId)] || null : null);

  return {
    actif: controle,
    verification,
    estRendu: (truckId) => estRendu(verification(truckId)),
    raison: (truckId) => raisonRendu(verification(truckId)),
  };
}

export default useVerificationFlotte;
